import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import apiClientFactory from "../../backend/apiClient";
import endpoints from "../../backend/endpoints";
import { getBuildingListStart } from "../../store/Building/actions";

const getAssignmentId = (assignment) =>
  assignment?.id || assignment?.assignmentId || assignment?.cleanerBuildingAssignmentId;

const getAssignmentBuildingId = (assignment) =>
  assignment?.buildingId || assignment?.BuildingId || assignment?.building?.id;

const CleanerBuildingAssignments = ({ cleanerId }) => {
  const dispatch = useDispatch();
  const staffUser = useSelector((state) => state.Auth.staffUser);
  const buildingState = useSelector((state) => state.buildings);
  const { selected_project } = useSelector((state) => state.project);

  const [assignments, setAssignments] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const buildings = useMemo(
    () => buildingState?.buildings?.data || [],
    [buildingState?.buildings]
  );

  const canManage = staffUser?.type === "admin" && cleanerId;

  const loadAssignments = useCallback(async () => {
    if (!cleanerId || staffUser?.type !== "admin") return;

    try {
      setLoading(true);
      const apiClient = apiClientFactory();
      const response = await apiClient.get(
        endpoints.cleanerBuildingAssignmentList({ cleanerId })
      );
      const data =
        response?.data?.response?.data ||
        response?.data?.response ||
        response?.data?.data ||
        [];
      setAssignments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load cleaner building assignments");
    } finally {
      setLoading(false);
    }
  }, [cleanerId, staffUser?.type]);

  useEffect(() => {
    if (selected_project?.id) {
      dispatch(getBuildingListStart({ projectId: selected_project.id }));
    }
  }, [dispatch, selected_project?.id]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const addAssignment = async () => {
    if (!selectedBuildingId) {
      toast.error("Please select a building.");
      return;
    }

    try {
      setSaving(true);
      const apiClient = apiClientFactory();
      await apiClient.post(endpoints.cleanerBuildingAssignmentCreate, {
        cleanerId: Number(cleanerId),
        buildingId: Number(selectedBuildingId),
      });
      toast.success("Building assigned to cleaner");
      setSelectedBuildingId("");
      loadAssignments();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to assign building");
    } finally {
      setSaving(false);
    }
  };

  const updateAssignment = async (assignment, buildingId) => {
    const id = getAssignmentId(assignment);
    if (!id || !buildingId) return;

    try {
      setSaving(true);
      const apiClient = apiClientFactory();
      await apiClient.put(endpoints.cleanerBuildingAssignmentUpdate, {
        id,
        cleanerId: Number(cleanerId),
        buildingId: Number(buildingId),
      });
      toast.success("Cleaner building assignment updated");
      loadAssignments();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update building assignment");
    } finally {
      setSaving(false);
    }
  };

  const deleteAssignment = async (assignment) => {
    const id = getAssignmentId(assignment);
    const buildingId = getAssignmentBuildingId(assignment);
    if (!id) return;

    try {
      setSaving(true);
      const apiClient = apiClientFactory();
      const payload = {
        id,
        cleanerId: Number(cleanerId),
      };
      if (buildingId) payload.buildingId = Number(buildingId);

      await apiClient.delete(endpoints.cleanerBuildingAssignmentDelete, {
        data: payload,
      });
      toast.success("Cleaner building assignment removed");
      loadAssignments();
    } catch (error) {
      console.error(error);
      toast.error("Failed to remove building assignment");
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) return null;

  return (
    <div style={{ marginTop: 20, width: "510px" }}>
      <h4 style={{ marginBottom: 10 }}>Assigned Buildings</h4>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <select
          className="form_input"
          style={{ flex: 1 }}
          value={selectedBuildingId}
          onChange={(e) => setSelectedBuildingId(e.target.value)}
          disabled={saving}
        >
          <option value="">Select Building</option>
          {buildings.map((building) => (
            <option key={building.id} value={building.id}>
              {building.building_number}
            </option>
          ))}
        </select>
        <button type="button" onClick={addAssignment} disabled={saving}>
          Add
        </button>
      </div>

      {loading ? (
        <p>Loading assigned buildings...</p>
      ) : assignments.length === 0 ? (
        <p style={{ color: "grey" }}>No buildings assigned.</p>
      ) : (
        assignments.map((assignment) => {
          const assignmentId = getAssignmentId(assignment);
          const buildingId = getAssignmentBuildingId(assignment);

          return (
            <div
              key={assignmentId || `${cleanerId}_${buildingId}`}
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <select
                className="form_input"
                style={{ flex: 1 }}
                value={buildingId || ""}
                onChange={(e) => updateAssignment(assignment, e.target.value)}
                disabled={saving}
              >
                <option value="">Select Building</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.building_number}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => deleteAssignment(assignment)}
                disabled={saving}
              >
                Remove
              </button>
            </div>
          );
        })
      )}
    </div>
  );
};

export default CleanerBuildingAssignments;
